import React from "react";
import Chart from "react-apexcharts";
import { Spin } from "antd";
import {
  convertToInternationalCurrencySystem,
  currencyDecimalMap,
} from "../../../helper";
import { GRAPH_TYPES } from "../../../store/types";
import {
  mergeChartOptions,
  mergeBarChartOptions,
  mergeAreaChartOptions,
} from "../../Compare/chartTheme";

/** Pick the shared chartTheme.ts merge helper matching the currently
 *  selected chart type — same helper SingleStockAnalysis's FinancialChart
 *  and newDynamicGraph.tsx use, so this chart inherits the app's dark/
 *  light chart styling instead of the legacy hand-rolled `graphColors`
 *  palette (a fixed teal/cyan set with no dark-mode/card treatment). */
const mergeFnForType = (type: "line" | "area" | "bar") =>
  type === "bar"
    ? mergeBarChartOptions
    : type === "area"
      ? mergeAreaChartOptions
      : mergeChartOptions;

interface ShareSplit {
  date: string;
  splitRatio: string;
}

interface GraphSeries {
  name: string;
  data: (number | null)[];
  metricGraphType: string;
  shareSplits?: ShareSplit[];
}

interface GraphData {
  series: GraphSeries[];
  categories: string[];
}

interface DynamicFinancialChartProps {
  graphData: GraphData | null;
  baseCurrency?: string;
  decimalPlaces?: number;
  percentageChartType?: "bar" | "line" | "area";
  absoluteChartType?: "bar" | "line" | "area";
  daysChartType?: "bar" | "line" | "area";
  multiplesChartType?: "bar" | "line" | "area";
  loading?: boolean;
}

const getRatioType = (splitRatio: string) => {
  const [first, second] = splitRatio.split(":").map(Number);
  return first > second ? "Consolidation" : "Split";
};

export const FinancialChart: React.FC<DynamicFinancialChartProps> = ({
  graphData,
  baseCurrency = "LKR",
  absoluteChartType = "bar",
  loading = false,
}) => {
  const decimalPlaces = currencyDecimalMap[baseCurrency] ?? 2;

  const absoluteValueChartData = React.useMemo(() => {
    if (!graphData || !Object.keys(graphData).length) return null;

    const absoluteSeries = graphData.series.filter(
      (s) => s.metricGraphType === GRAPH_TYPES.ABSOLUTE_VALUES,
    );

    if (!absoluteSeries.length) return null;

    const allAnnotations = graphData.series.flatMap((series) =>
      (series.shareSplits ?? []).map((split) => ({
        x: split.date,
        borderColor: "var(--vi-accent)",
        label: {
          position: "bottom",
          style: {
            color: "var(--vi-accent-on)",
            background: "var(--vi-accent)",
          },
        },
      })),
    );

    return {
      series: absoluteSeries,
      options: mergeFnForType(absoluteChartType)({
        chart: {
          type: absoluteChartType,
          id: "dynamic-graph",
        },
        dataLabels: {
          enabled: absoluteSeries.length === 1,
          formatter: (value: any) => {
            if (value === 0) return value;
            if (value != null)
              return convertToInternationalCurrencySystem(value, decimalPlaces);
          },
          offsetY: absoluteChartType === "bar" ? -20 : -10,
        },
        yaxis: {
          labels: {
            formatter: (value: any) => {
              if (
                value === null ||
                value === undefined ||
                isNaN(parseFloat(value))
              )
                return "";
              return `${baseCurrency} ${convertToInternationalCurrencySystem(value, decimalPlaces)}`;
            },
          },
        },
        xaxis: { categories: graphData.categories },
        tooltip: {
          y: {
            formatter: (val: any, opts: any) => {
              if (val === null || val === undefined || isNaN(val)) return "N/A";

              const xValue = opts.w.globals.categoryLabels[opts.dataPointIndex];
              const seriesName = opts.w.globals.seriesNames[opts.seriesIndex];
              const currentSeries = (
                opts.w.config.series as GraphSeries[]
              ).find((s) => s.name === seriesName);

              const shareSplit = currentSeries?.shareSplits?.find(
                (split) =>
                  split.date === xValue ||
                  new Date(split.date).toDateString() ===
                    new Date(xValue).toDateString(),
              );

              let formattedValue = `${baseCurrency} ${convertToInternationalCurrencySystem(val, decimalPlaces)}`;

              if (shareSplit) {
                const ratioType = getRatioType(shareSplit.splitRatio);
                formattedValue += ` 📊 ${ratioType} Ratio: ${shareSplit.splitRatio}`;
              }

              return formattedValue;
            },
          },
        },
        legend: {
          showForSingleSeries: false,
          formatter: (seriesName: string) => seriesName.split(":")[0].trim(),
        },
        annotations: { xaxis: allAnnotations },
      }),
    };
  }, [graphData, baseCurrency, decimalPlaces, absoluteChartType]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <Spin size="large" />
      </div>
    );
  }

  if (!graphData?.series?.length) {
    return (
      <div className="flex justify-center items-center py-16 text-gray-400 text-sm">
        No chart data available.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 w-full">
      {absoluteValueChartData && (
        <div className="w-full vi-glass-card">
          <Chart
            key={absoluteChartType}
            options={absoluteValueChartData.options}
            series={absoluteValueChartData.series}
            height={550}
            type={absoluteChartType}
          />
        </div>
      )}
    </div>
  );
};
